<template>
  <div v-if="dataLoaded">
    <!-- Row top buttons -->
    <div class="row q-mb-lg">
      <div class="col-6" style="justify-content: flex-start">
        <q-btn
          class="capitalize ctrl-button"
          color="grey-5"
          :label="`${$store.state.stringsStore.strings.action.toOverview}`"
          @click="$router.push('/netpricepromotion')"
          icon="keyboard_backspace"
        />
      </div>
      <div class="col-6">
        <q-btn
          style="float: right"
          class="capitalize ctrl-button right q-ml-md"
          :color="'ctrl-turquoise'"
          :disable="false"
          :label="`${$store.state.stringsStore.strings.action.save}`"
          @click="saveChanges()"
          icon-right="save"
        />
        <q-btn
          v-if="data.id"
          class="q-ml-md capitalize ctrl-button right"
          icon-right="delete"
          color="negative capitalize"
          :label="$store.state.stringsStore.strings.action.delete"
          align="right"
          @click="deletePromotion()"
        />
      </div>
    </div>

    <!-- Left -->
    <div class="col-md-6 col-sm-12 col-xs-12">
      <div class="row q-mb-sm">
        <div class="full-width">
          <p class="card-title">Details</p>

          <div class="card-block">
            <div class="row">
              <div class="col-md-4 col-sm-12">
                <q-input
                  class="q-mr-md"
                  color="primary"
                  v-model="data.name"
                  :float-label="getString('name')"
                />
              </div>
              <div class="col-md-4 col-sm-6">
                <!-- Start date -->
                <q-datetime
                  format24h
                  :max="data.date.end"
                  class="q-mr-md"
                  :float-label="startDate"
                  v-model="data.date.start"
                />
              </div>
              <div class="col-md-4 col-sm-6">
                <!-- End date -->
                <q-datetime
                  format24h
                  :min="data.date.start"
                  :float-label="endDate"
                  v-model="data.date.end"
                  clearable
                />
              </div>
            </div>

            <div class="row">
              <chips-autocomplete
                :error="false"
                :errorLabel="
                  $store.state.stringsStore.language === 'nl'
                    ? 'Dit veld is verplicht'
                    : 'This field is required'
                "
                class="q-pb-md full-width"
                :selection="data.accounts"
                :name="'Accounts'"
                :labelTerm="'name'"
                :endpoint="'account'"
                :resultLabel="'accounts'"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Right -->
      <div class="col-md-12 col-sm-12 col-xs-12 q-mt-md">
        <div class="row q-mb-sm">
          <div class="full-width">
            <p class="card-title">
              {{ 'Rules' }}
            </p>

            <q-btn
              class="ctrl-button capitalize q-mb-md"
              color="primary"
              @click="addNewRule"
              :label="getString('module.promotion.rule.new')"
            />

            <div
              class="card-block q-mb-sm"
              :class="{ 'rule-deleted': rule.deleted }"
              v-for="(rule, r) in combinedRules"
              :key="`rule-${r}`"
            >
              <div class="row">
                <!-- Item -->
                <div class="col-md-6 col-sm-12">
                  <q-search
                    type="text"
                    v-model="rule.item.code"
                    placeholder="Item"
                    class="q-mr-md"
                    :disable="disable"
                    float-label="Item"
                  >
                    <q-autocomplete
                      :max-results="25"
                      :min-characters="2"
                      @search="search"
                      @selected="selected(...arguments, rule)"
                    />
                  </q-search>
                </div>

                <!-- Gross price -->
                <div class="col-md-3 col-sm-6">
                  <q-input
                    readonly
                    disable
                    class="q-mr-md"
                    color="primary"
                    v-model="rule.item.gross_price"
                    :float-label="getString('price.gross')"
                  />
                </div>

                <!-- Price -->
                <div class="col-md-3 col-sm-6">
                  <q-input
                    class="q-mr-md"
                    color="primary"
                    v-model="rule.item.net_price"
                    :float-label="getString('price.net')"
                  />
                </div>
              </div>

              <q-btn
                v-if="!rule.deleted"
                class="ctrl-button capitalize q-mb-md q-mt-md"
                color="negative"
                @click="removeRule(r)"
                :label="getString('action.delete')"
              />

              <q-btn
                v-if="rule.deleted"
                class="ctrl-button capitalize q-mb-md q-mt-md"
                color="positive"
                @click="restoreRule(r)"
                label="restore"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import qs from 'qs';
import moment from 'moment';

// Mixins
import getString from '@/helpers/GetStrings';
import errorHandler from '@/mixins/detailViews/errorHandler';
import ChipsAutocomplete from '@/components/ChipsAutocomplete.vue';
import EventBus from '@/event-bus';

export default {
  components: {
    ChipsAutocomplete,
  },
  mixins: {
    errorHandler,
  },
  data() {
    return {
      // Name of the module defined in user rights. eg. blog for blog.edit, blog.add etc..
      permission: 'netpricepromotion',
      // Where data is fetched from
      endpoint: 'netpricepromotion',
      // The object key from the response object
      responseData: {
        single: 'netpricepromotion',
        multiple: 'netpricepromotions',
      },

      readOnly: false,
      disable: false,
      dataLoaded: false,

      // Used for navigation on the front-end (matching the router)
      module: 'netpricepromotion',

      data: {
        name: '',
        date: {
          start: Date.now(),
          end: null,
        },
        rules: [],
        accounts: [],
      },

      newRules: [],
      rules: [],

      // When an existing rule is removed, it is added to this array for a delete on save.
      rulesRemoved: [],
    };
  },

  methods: {
    getString,

    async search(terms, done) {
      try {
        // Request items based on terms given
        const results = await this.axios.get(`item?autocomplete=${terms.toLowerCase()}`);

        // Map only the item names/labels... to the suggested array
        const suggested = results.data.items.map(item => ({
          label: item.code,
          id: item.id,
          gross_price: item.gross_price,
        }));

        // Suggested items found, return suggestions.
        if (suggested.length) {
          done(suggested);
        }

        // Since an error is thrown when no items are found in autocomplete -> promt new suggestion here.
      } catch (error) {
        done([]);
      }
    },

    selected(item, keyboard, rule) {
      // Prevent adding on keyboard arrow down
      if (keyboard) return;

      rule.item.id = item.id;
      rule.item.code = item.label;
      rule.item.gross_price = item.gross_price;

      this.inputField = '';
    },

    addNewRule() {
      this.newRules.unshift({
        item: {
          id: null,
          net_price: null,
          gross_price: null,
        },
      });
    },

    fetchPromotion() {
      this.dataLoaded = false;

      this.axios.get(`netpricepromotion/${this.$route.params.id}`).then(async res => {
        this.data = res.data.promotion;

        this.rules = await Promise.all(this.data.rules.map(rule => this.getRuleData(rule.id)));

        this.dataLoaded = true;
      });
    },

    deletePromotion() {
      this.axios.delete(`netpricepromotion/${this.$route.params.id}`).then(() => {
        this.$router.push('/netpricepromotion');
      });
    },

    updateAccounts(newVal) {
      this.data.accounts = newVal;
    },

    requestParams() {
      return {};
    },

    removeRule(index) {
      const rule = this.combinedRules[index];
      // Move only existing rules to rulesRemoved array.
      if (rule.id) {
        this.rulesRemoved.push(rule);
        // Set deleted to true in the filtered array.
        this.$set(this.rules[index], 'deleted', true);
      } else {
        this.newRules.splice(index, 1);
      }
    },

    restoreRule(index) {
      const rule = this.rules[index];
      // Remove the rule from the rulesRemoved array.
      this.rulesRemoved = this.rulesRemoved.filter(item => item.id !== rule.id);

      this.$set(this.rules[index], 'deleted', false);
    },

    async getRuleData(ruleId) {
      const rule = await this.axios.get(
        `netpricepromotion/${this.$route.params.id}/rule/${ruleId}`,
      );
      // Rule type order needs to be handled seperately

      if (rule.data.rule.type === 'order') {
        rule.data.rule.order = rule.data.rule.data;
      }
      return rule.data.rule;
    },

    /**
     * Saving a promotion requires saving the promotion itself as well as each rule within the promotion.
     */
    saveChanges() {
      if (this.$route.params.id) {
        this.saveExistingPromotion();
      } else {
        this.saveNewPromotion();
      }
    },

    cleanData(data) {
      Object.keys(data).forEach(key => {
        if (!data[key] && data[key] !== false) {
          delete data[key];
        }
      });
      return data;
    },

    saveExistingPromotion() {
      let data = {
        name: this.data.name,
        account:
          this.data.accounts && this.data.accounts.length
            ? this.data.accounts.map(account => account.id)
            : null,
        date_start: moment(this.data.date.start).format('YYYY-MM-DD'),
        date_end: this.data.date.end ? moment(this.data.date.end).format('YYYY-MM-DD') : null,
      };
      data = this.cleanData(data);
      this.axios
        .put(
          `netpricepromotion/${this.$route.params.id}`,
          qs.stringify(data, { arrayFormat: 'brackets', indices: false }),
        )
        .then(async () => {
          // Save all rules.
          await Promise.all(
            this.combinedRules.map(rule => {
              // Save all existing rules.
              if (rule.id) {
                if (!rule.deleted) {
                  return this.saveExistingRule(rule);
                }
                return true;
              }
              // Save all new rules.
              return this.saveNewRule(rule);
            }),
          )
            .then(() => {
              this.newRules = [];
            })
            .catch(err => {
              // Handle the error (handleError mixin)
              this.handleError(err);
            });

          // Apply deletes
          await Promise.all(
            this.rulesRemoved.map(rule =>
              this.axios.delete(`netpricepromotion/${this.$route.params.id}/rule/${rule.id}`),
            ),
          );

          this.$q.notify({
            message: getString('module.promotion.edit.save.success'),
            type: 'positive',
            timeout: 1200,
            position: 'top-right',
          });

          this.fetchPromotion();
        });
    },

    saveNewPromotion() {
      let data = {
        name: this.data.name,
        account:
          this.data.accounts && this.data.accounts.length
            ? this.data.accounts.map(account => account.id)
            : null,
        date_start: moment(this.data.date.start).format('YYYY-MM-DD'),
        date_end: this.data.date.end ? moment(this.data.date.end).format('YYYY-MM-DD') : null,
      };

      let location;
      let newId;

      data = this.cleanData(data);
      this.axios
        .post('netpricepromotion', qs.stringify(data, { arrayFormat: 'brackets', indices: false }))
        .then(response => {
          // Redirect to detail page of the newly created item
          if (response.headers && response.headers.location) {
            location = response.headers.location.replace('/api', '');
            newId = response.headers.location.split('/').pop();
          }
        })
        .then(async () => {
          // id availible, post all rules.
          await Promise.all(this.combinedRules.map(rule => this.saveNewRule(rule, newId)))
            .then(() => {
              this.newRules = [];
            })
            .catch(err => {
              // Handle the error (handleError mixin)
              this.handleError(err);
            })
            .finally(() => {
              this.$router.push(location);
              this.$nextTick(() => {
                this.fetchPromotion();
              });
            });

          // Successfully saved all new rules.
          this.$q.notify({
            message: getString('module.promotion.edit.save.success'),
            type: 'positive',
            timeout: 1200,
            position: 'top-right',
          });
        })
        .catch(err => {
          // Handle the error (handleError mixin)
          this.handleError(err);
        });
    },

    saveNewRule(rule, ruleId = null) {
      let ruleData = {
        item: rule.item.id,
        net_price: rule.item.net_price,
      };

      ruleData = this.cleanData(ruleData);

      return this.axios.post(
        `netpricepromotion/${ruleId || this.$route.params.id}/rule`,
        qs.stringify(ruleData, { plainObjects: true }),
      );
    },

    saveExistingRule(rule) {
      let ruleData = {
        item: rule.item.id,
        net_price: rule.item.net_price,
      };

      ruleData = this.cleanData(ruleData);

      return this.axios.put(
        `netpricepromotion/${this.$route.params.id}/rule/${rule.id}`,
        qs.stringify(ruleData, { plainObjects: true }),
      );
    },
  },

  computed: {
    combinedRules() {
      return [...this.newRules, ...this.rules];
    },

    startDate() {
      return this.$store.state.stringsStore.language === 'nl' ? 'Startdatum' : 'Start date';
    },

    endDate() {
      return this.$store.state.stringsStore.language === 'nl' ? 'Einddatum' : 'End date';
    },
  },

  mounted() {
    EventBus.$on('chips-autocomplete:accounts', selection => {
      this.data.accounts = selection;
    });

    if (this.$route.params.id) {
      this.fetchPromotion();

      return;
    }

    this.dataLoaded = true;
  },
};
</script>

<style lang="stylus">
.card-block.rule-deleted
    background rgba(255, 255, 255, 0.5)
</style>
